// services/documentCacheService.js
import { useQueryClient } from 'react-query';

/**
 * Service for managing smart document cache invalidation
 */
class DocumentCacheService {
  constructor() {
    this.queryClient = null;
    this.pendingDocumentIds = new Set(); // Track pending documents for selective updates
  }

  setQueryClient(queryClient) {
    this.queryClient = queryClient;
  }

  /**
   * Add a document ID to the pending documents list for selective updates
   */
  addPendingDocument(documentId) {
    this.pendingDocumentIds.add(documentId);
    console.log(`📌 Added document ${documentId} to pending tracking`);
  }

  /**
   * Remove a document ID from pending tracking (when it's completed)
   */
  removePendingDocument(documentId) {
    this.pendingDocumentIds.delete(documentId);
    console.log(`✅ Removed document ${documentId} from pending tracking`);
  }

  /**
   * Invalidate cache based on document status and context
   */
  invalidateDocumentCache(documentId, newStatus, context = {}) {
    if (!this.queryClient) {
      console.warn('QueryClient not set in DocumentCacheService');
      return;
    }

    console.log(`🔄 Smart cache invalidation for document ${documentId}, status: ${newStatus}`);

    // Always invalidate specific document queries
    this.queryClient.invalidateQueries(['document', documentId]);

    // Smart invalidation based on status change
    switch (newStatus) {
      case 'completed':
        // Document completed - invalidate all document queries so completed tab updates immediately
        this.queryClient.invalidateQueries(['documents']);
        // Also refresh stats so counters flip right away
        this.queryClient.invalidateQueries(['documentStats']);
        this.removePendingDocument(documentId);
        break;

      case 'pending_signature':
        // Document moved to pending - invalidate all views except completed
        this.queryClient.invalidateQueries(['documents'], {
          predicate: (query) => {
            const [, , , , , statusFilter] = query.queryKey;
            return statusFilter !== 'completed';
          }
        });
        // Also refresh stats so counters reflect new pending
        this.queryClient.invalidateQueries(['documentStats']);
        this.addPendingDocument(documentId);
        break;

      case 'draft':
        // Document is draft - only invalidate draft and all views
        this.queryClient.invalidateQueries(['documents'], {
          predicate: (query) => {
            const [, , , , , statusFilter] = query.queryKey;
            return !statusFilter || statusFilter === 'draft';
          }
        });
        break;

      default:
        // Unknown status - invalidate all to be safe
        this.queryClient.invalidateQueries(['documents']);
    }
  }

  /**
   * Handle signature events - called from DocumentViewer when signatures are added
   */
  onSignatureAdded(documentId, signatureData) {
    console.log(`✍️ Signature added to document ${documentId}`);
    
    // Check if this signature completes the document
    if (signatureData.completesDocument) {
      this.invalidateDocumentCache(documentId, 'completed', { signatureAdded: true });
    } else {
      // Still pending - just invalidate pending views
      this.queryClient?.invalidateQueries(['documents'], {
        predicate: (query) => {
          const [, , , , , statusFilter] = query.queryKey;
          return statusFilter === 'pending_signature' || !statusFilter;
        }
      });
    }
  }

  /**
   * Selectively refresh only pending documents
   */
  refreshPendingDocuments() {
    if (!this.queryClient) return;

    console.log(`🔄 Refreshing ${this.pendingDocumentIds.size} pending documents`);
    
    // Only invalidate queries that might contain pending documents
    this.queryClient.invalidateQueries(['documents'], {
      predicate: (query) => {
        const [, , , , , statusFilter] = query.queryKey;
        return statusFilter === 'pending_signature' || statusFilter === 'all' || !statusFilter;
      }
    });
    // Keep stats fresh, too
    this.queryClient.invalidateQueries(['documentStats']);
  }

  /**
   * Get cache configuration based on document status mix
   */
  static getCacheConfigForStatus(statusFilter, documentComposition = {}) {
    const { completed = 0, pending = 0, draft = 0 } = documentComposition;
    
    // For specific status filters
    if (statusFilter === 'completed') {
      return {
        staleTime: 30 * 60 * 1000, // 30 minutes - completed docs rarely change
        cacheTime: 2 * 60 * 60 * 1000, // 2 hours cache time
        refetchOnWindowFocus: false,
        refetchInterval: false,
      };
    }
    
    if (statusFilter === 'draft') {
      return {
        staleTime: 15 * 60 * 1000, // 15 minutes - drafts change occasionally
        cacheTime: 60 * 60 * 1000, // 1 hour cache time
        refetchOnWindowFocus: false,
        refetchInterval: false,
      };
    }
    
    if (statusFilter === 'pending_signature') {
      return {
        staleTime: 30 * 1000, // 30 seconds - pending docs change frequently
        cacheTime: 5 * 60 * 1000, // 5 minutes cache time
        refetchOnWindowFocus: true,
        refetchInterval: 2 * 60 * 1000, // Auto-refresh every 2 minutes
      };
    }
    
    // For mixed views, optimize based on composition
    const totalDocs = completed + pending + draft;
    if (totalDocs === 0) {
      return {
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchInterval: false,
      };
    }
    
    const pendingRatio = pending / totalDocs;
    
    if (pendingRatio > 0.5) {
      // Mostly pending documents
      return {
        staleTime: 1 * 60 * 1000, // 1 minute
        cacheTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchInterval: 3 * 60 * 1000, // Every 3 minutes
      };
    } else if (pendingRatio > 0.2) {
      // Some pending documents
      return {
        staleTime: 2 * 60 * 1000, // 2 minutes
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchInterval: 5 * 60 * 1000, // Every 5 minutes
      };
    } else {
      // Mostly completed/draft documents
      return {
        staleTime: 10 * 60 * 1000, // 10 minutes
        cacheTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchInterval: false,
      };
    }
  }
}

// Create singleton instance
export const documentCacheService = new DocumentCacheService();

// React hook for using the cache service
export const useDocumentCache = () => {
  const queryClient = useQueryClient();
  
  // Set query client on first use
  if (!documentCacheService.queryClient) {
    documentCacheService.setQueryClient(queryClient);
  }
  
  return documentCacheService;
};

export default documentCacheService;